"use client";

import * as React from "react";
import { Info, Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import {
  isCourseSource,
  isExternalSource,
  isURLSource,
  StudyNotUploadedDocument,
  UploadableSource,
  UploadableURL,
} from "../../types";
import { cn } from "@/lib/utils";
import { CourseSourcesTable } from "./course-sources-table";
import { useNotebookContext } from "../../hooks/useNotebook";
import { CanvasCourse } from "@/lib/types";
import { useUserLimits } from "../../hooks/useUserLimits";
import { ThemedTabsList, ThemedTabsTrigger } from "./themed-tabs";
import { ExternalSourcesTab } from "./external-sources-tab";
import { AddLinkDialogContent } from "./add-link-dialog-content";
import { AddPasteTextDialogContent } from "./add-paste-text-dialog-content";
import { IconCanvasLogoSolid } from "@instructure/ui-icons";
import {
  CONTENT_TYPE_PRIORITY,
  CourseSourcesTableB,
} from "./course-sources-table-b";
import { getFileSizeNumeric } from "./sources/source-viewer";

interface AddSourcesModalProps {
  open: boolean;
  courses: CanvasCourse[];
  onClose: () => void;
  uploadSource?: (source: UploadableSource) => void;
  init: boolean;
}

type InvalidFileReason = "size" | "type";

type InvalidFile = {
  file: File;
  reason: InvalidFileReason;
  message: string;
};

export function AddSourcesModalB({
  open,
  courses,
  onClose,
  uploadSource,
  init,
}: AddSourcesModalProps) {
  const { notebook, sources, courseFiles, loadingSources } =
    useNotebookContext();
  const { limits } = useUserLimits();

  const courseName = courses.find(
    (c) => c.id.toString() === notebook.course_id
  )?.default;

  const [selectedUploadableSources, setSelectedUploadableSources] =
    React.useState<UploadableSource[]>([]);

  const [invalidFiles, setInvalidFiles] = React.useState<InvalidFile[]>([]);
  const [modalState, setModalState] = React.useState<"main" | "link" | "text">(
    "main"
  );

  const [selectedTab, setSelectedTab] = React.useState<"own" | "course">(
    "course"
  );

  const allowedMimeTypes = React.useMemo(
    () => limits.supportedTypes.map((t) => t.mime),
    [limits]
  );

  const allowedFileExtensions = limits.supportedTypes.map((l) =>
    l.extensions.join(", ")
  );

  const maxFileSizeBytes = limits.maxFileSize;

  // Auto-select the 3 most recent course files on mount
  React.useEffect(() => {
    if (!loadingSources && courseFiles.length > 0 && sources.length === 0) {
      // Sort by modification date (most recent first) and take top 3
      const sortedFiles = [...courseFiles]
        .filter((file) => {
          // Only include files that meet size and type requirements
          return (
            allowedMimeTypes.includes(file.metadata["content_type"]) &&
            getFileSizeNumeric(file) <= maxFileSizeBytes &&
            !sources.find(
              (s) =>
                "canvas_file_id" in s.metadata &&
                s.metadata.canvas_file_id == file.metadata.canvas_file_id
            )
          );
        })
        .sort((a, b) => {
          // 2) Content-type relevance (if present)
          const aType = a.metadata?.content_type;
          const bType = b.metadata?.content_type;

          const aPriority =
            aType && aType in CONTENT_TYPE_PRIORITY
              ? CONTENT_TYPE_PRIORITY[aType]
              : Number.MAX_SAFE_INTEGER;

          const bPriority =
            bType && bType in CONTENT_TYPE_PRIORITY
              ? CONTENT_TYPE_PRIORITY[bType]
              : Number.MAX_SAFE_INTEGER;

          if (aPriority !== bPriority) return aPriority - bPriority;
          const dateA = new Date(a.metadata["created_at"]).getTime();
          const dateB = new Date(b.metadata["created_at"]).getTime();
          return dateB - dateA;
        });

      if (sortedFiles.length > 0) {
        setSelectedUploadableSources(sortedFiles.slice(0, 1));
      }
    }
  }, [
    loadingSources,
    sources,
    courseFiles,
    allowedMimeTypes,
    maxFileSizeBytes,
  ]);

  const handleConfirm = async () => {
    console.log(
      "uploading",
      selectedCourseSources,
      selectedExternalSources,
      selectedLinkSources
    );
    selectedCourseSources.forEach(uploadSource);
    selectedExternalSources.forEach(uploadSource);
    selectedLinkSources.forEach((link) => uploadSource(link));

    handleOpenChange();
  };

  function handleOpenChange() {
    setSelectedUploadableSources([]);
    setInvalidFiles([]);
    onClose();
    // Delay resetting modal state to prevent flash during close animation
    setTimeout(() => {
      setModalState("main");
    }, 300);
  }

  const selectedCourseSources = selectedUploadableSources.filter(
    isCourseSource
  ) as StudyNotUploadedDocument[];
  const selectedExternalSources = selectedUploadableSources.filter(
    isExternalSource
  ) as File[];
  const selectedLinkSources = selectedUploadableSources.filter(
    isURLSource
  ) as UploadableURL[];

  // auto-confirm when uploading files
  React.useEffect(() => {
    if (selectedExternalSources.length || selectedLinkSources.length)
      handleConfirm();
  }, [selectedExternalSources, selectedLinkSources]);

  const confirmButtonIsDisabled =
    (selectedTab === "own" &&
      selectedExternalSources.length === 0 &&
      selectedLinkSources.length === 0) ||
    (selectedTab === "course" && selectedCourseSources.length === 0);

  const totalSelectedCount = loadingSources
    ? 0
    : selectedUploadableSources.length;

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          handleOpenChange();
        }
      }}
    >
      <DialogContent className="bg-theme-0 overflow-y-scroll max-h-screen sm:max-w-[600px]">
        {modalState === "main" ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-left">
                <div className="text-sm text-theme-2 mb-1">{courseName}</div>
                <div className="text-2xl font-semibold">
                  {sources.length
                    ? "Add sources to your study set"
                    : "Let's build your study set"}
                </div>
                <div className="text-sm font-normal text-theme-1 mt-1">
                  Select or upload some files. We'll use these to create your
                  personalized study materials!
                </div>
              </DialogTitle>
            </DialogHeader>

            <Tabs value={selectedTab} className="w-full min-w-0">
              <ThemedTabsList className="mx-auto mb-2">
                <ThemedTabsTrigger
                  value="course"
                  onClick={() => setSelectedTab("course")}
                >
                  <IconCanvasLogoSolid />
                  Class files
                </ThemedTabsTrigger>
                <ThemedTabsTrigger
                  value="own"
                  onClick={() => setSelectedTab("own")}
                >
                  Add your own
                </ThemedTabsTrigger>
              </ThemedTabsList>

              <TabsContent value="course" className="w-full min-w-0">
                {loadingSources ? (
                  <div className="flex h-40 items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-theme-2" />
                  </div>
                ) : (
                  <CourseSourcesTableB
                    allowedMimeTypes={allowedMimeTypes}
                    maxFileSizeBytes={maxFileSizeBytes}
                    sources={sources}
                    files={courseFiles}
                    selected={selectedCourseSources}
                    setSelected={setSelectedUploadableSources}
                  />
                )}
              </TabsContent>

              <TabsContent value="own" className="space-y-4 w-full">
                <ExternalSourcesTab
                  allowedMimeTypes={allowedMimeTypes}
                  allowedFileExtensions={allowedFileExtensions}
                  maxFileSizeBytes={maxFileSizeBytes}
                  selectedSources={selectedUploadableSources}
                  setSelectedSources={setSelectedUploadableSources}
                  invalidFiles={invalidFiles}
                  setInvalidFiles={setInvalidFiles}
                  onOpenLinkDialog={() => setModalState("link")}
                  onOpenPasteTextDialog={() => setModalState("text")}
                />
              </TabsContent>
            </Tabs>

            {sources.length === 0 && (
              <div
                className={cn(
                  "flex items-center gap-2 rounded-lg border px-3 py-2",
                  "bg-emerald-200/80 text-emerald-900 border-emerald-300",
                  "text-xs leading-snug"
                )}
              >
                <Info className="h-3.5 w-3.5 flex-shrink-0 text-emerald-900" />
                <span>
                  {totalSelectedCount > 0
                    ? `Using ${totalSelectedCount} selected ${
                        totalSelectedCount === 1 ? "source" : "sources"
                      } to create your study set. You can always add more later.`
                    : "Select or upload at least one source to get started"}
                </span>
              </div>
            )}

            <div className="flex justify-between pt-4">
              {!init ? (
                <Button variant="outline" onClick={handleOpenChange}>
                  Back
                </Button>
              ) : (
                <div />
              )}
              <Button
                onClick={handleConfirm}
                disabled={confirmButtonIsDisabled}
                className={
                  "bg-brand-gradient text-black hover:scale-110 transition"
                }
              >
                Continue
              </Button>
            </div>
          </>
        ) : modalState === "link" ? (
          <AddLinkDialogContent
            onBack={() => setModalState("main")}
            onClose={handleOpenChange}
            selectedSources={selectedUploadableSources}
            setSelectedSources={setSelectedUploadableSources}
          />
        ) : (
          <AddPasteTextDialogContent
            onBack={() => setModalState("main")}
            onClose={handleOpenChange}
            onAddFile={(file) => {
              setSelectedUploadableSources((prev) => [...prev, file]);
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
